/**
 * Stormcaller talents (ranged DPS), rotation rework (docs/design/classes/stormcaller.md §5).
 * Tier 1: AoE spread vs single-target Flame Shock play, Lava Surge banking and movement.
 * Tier 2: spender follow-ups, Static Charge, interrupts and control.
 * Tier 3: Echo of the Elements and the Thundercrash transform, plus three capstones: Storm
 * Unleashed (Earthquake AoE), Master of Flame (single target) and Ascendance (burst transform).
 *
 * Lava Surge is baseline now (the old `t1.lava_surge` node is gone, `t3.searing_flames` is cut and
 * `t3.stormwalker` became Ascendance); stale ids are dropped on load. Class auras are copied with
 * `auraRef` so every inline copy stays identical.
 */
import { ABILITY, AURA, CLASS, PASSIVE } from '@keystone/content';
import type { AbilityDef, AuraDef, EffectSpec, ProcSpec } from '@keystone/sim';
import { auraRef } from '../../transforms';
import type { TalentNode } from '../../types';
import { nearTarget, nodeFor, procPassive, resource, stun } from './common';

const S = ABILITY.stormcaller;
const node = nodeFor(CLASS.stormcaller);
export const STORMCALLER_CAPSTONE = 'stormcaller.capstone';

/** Talent-only transform forms and their "ready" auras (not in ABILITY: they exist only with the talent). */
export const STORMCALLER_TALENT_IDS = {
  thundercrash: 'stormcaller.thundercrash',
  thundercrashReady: 'stormcaller.thundercrash_ready',
  lavaBeam: 'stormcaller.lava_beam',
  lavaBeamReady: 'stormcaller.lava_beam_ready',
  surgeOfPower: 'stormcaller.surge_of_power',
} as const;
const T = STORMCALLER_TALENT_IDS;

/** Identical copy of Flame Shock's DoT (the registry keys auras by id: keep it identical). */
const FLAME_SHOCK: AuraDef = {
  id: AURA.flameShock,
  name: 'Flame Shock',
  kind: 'debuff',
  durationMs: 18000,
  stacking: 'refresh',
  pandemic: true,
  periodic: { intervalMs: 2000, hasteScaled: true, effects: [{ type: 'damage', school: 'fire', amount: { coef: 0.9 } }] },
};

const selfApply = (aura: AuraDef, stacks?: number): EffectSpec => ({ type: 'applyAura', to: 'self', aura, ...(stacks ? { stacks } : {}) });
const staticCharge = (): EffectSpec => selfApply(auraRef(AURA.staticCharge));

/** Echo of the Elements: a Lava Surge granted while one is already up refunds 10 Maelstrom. */
const echoRefund: EffectSpec = { type: 'conditional', cond: { type: 'selfHasAura', auraId: AURA.lavaSurge }, then: [resource('maelstrom', 10)] };

/** The class's Lava Surge roll (content: stormcaller.lava_surge_passive), with the Echo refund in front. */
const lavaSurgeRollWithEcho: ProcSpec = {
  on: 'damageDealt',
  abilityIds: [S.flameShock],
  chance: 0.06,
  icdMs: 4000,
  badLuck: { step: 0.03 },
  effects: [echoRefund, { type: 'modifyCooldown', to: 'self', abilityId: S.lavaBurst, addCharge: true }, selfApply(auraRef(AURA.lavaSurge))],
};

/** Surge of Power: Earth Shock makes the next Lightning Bolt instant. */
const SURGE_OF_POWER: AuraDef = {
  id: T.surgeOfPower,
  name: 'Surge of Power',
  kind: 'buff',
  durationMs: 10000,
  stacking: 'refresh',
  mods: [{ stat: 'castTimePct', value: -1, abilityIds: [S.lightningBolt], ignoreStacks: true }],
  procs: [{ on: 'castResolved', abilityIds: [S.lightningBolt], consumeStack: true, effects: [] }],
  ui: { proc: true },
};

/** Thundercrash transform: a full Arc Torrent turns key 6 into Thundercrash for 8 s. */
const THUNDERCRASH_READY: AuraDef = {
  id: T.thundercrashReady,
  name: 'Thundercrash',
  kind: 'buff',
  durationMs: 8000,
  stacking: 'refresh',
  overrides: [{ abilityId: S.arcTorrent, with: T.thundercrash }],
  ui: { proc: true },
};

const THUNDERCRASH: AbilityDef = {
  id: T.thundercrash,
  name: 'Thundercrash',
  school: 'nature',
  description: 'Replaces Arc Torrent for 8 s after a full channel. Instantly crash down on the target and nearby enemies. Generates 20 Maelstrom.',
  targeting: 'enemy',
  range: 40,
  requiresFacing: true,
  usableIf: { type: 'selfHasAura', auraId: T.thundercrashReady },
  gain: [{ resource: 'maelstrom', amount: 20 }],
  effects: [
    { type: 'damage', school: 'nature', amount: { coef: 6.0, variance: 0.05 } },
    { type: 'damage', to: 'area', area: nearTarget(8, 8), school: 'nature', amount: { coef: 3.0, variance: 0.05 } },
    { type: 'removeAura', to: 'self', auraId: T.thundercrashReady },
  ],
  ui: { tags: ['aoe'], color: '#7fd3ff', animAs: S.arcTorrent },
};

/** Ascendance: Stormkeeper also turns Lava Burst into Lava Beam for 12 s. */
const ASCENDANCE: AuraDef = {
  id: T.lavaBeamReady,
  name: 'Ascendance',
  kind: 'buff',
  durationMs: 12000,
  stacking: 'refresh',
  overrides: [{ abilityId: S.lavaBurst, with: T.lavaBeam }],
  ui: { proc: true },
};

const LAVA_BEAM: AbilityDef = {
  id: T.lavaBeam,
  name: 'Lava Beam',
  school: 'fire',
  description: 'Replaces Lava Burst during Ascendance. Instant, no cooldown: burns the target and up to 4 nearby enemies. Generates 8 Maelstrom.',
  targeting: 'enemy',
  range: 40,
  requiresFacing: true,
  usableIf: { type: 'selfHasAura', auraId: T.lavaBeamReady },
  gain: [{ resource: 'maelstrom', amount: 8 }],
  effects: [
    { type: 'damage', school: 'fire', amount: { coef: 5.0, variance: 0.05 } },
    { type: 'damage', to: 'area', area: nearTarget(8, 4), school: 'fire', amount: { coef: 2.5, variance: 0.05 } },
  ],
  ui: { tags: ['aoe'], color: '#ff7a3d', animAs: S.lavaBurst },
};

/** Master of the Elements with Lightning Bolt added (Master of Flame). Same shape as the content aura. */
const MOTE_WITH_BOLT = [S.earthShock, S.tempest, S.chainLightning, S.lightningBolt];

export const STORMCALLER_TREE: readonly TalentNode[] = [
  // ---------------------------------------------------------------- tier 1
  node({
    id: 'stormcaller.t1.forked',
    name: 'Forked Lightning',
    description: 'Chain Lightning hits 2 more targets but deals 20% less damage. Tempest hits 1 more target.',
    tier: 1,
    col: 0,
    cost: 2,
    effect: [
      { op: 'modifyArea', abilityId: S.chainLightning, maxTargetsDelta: 2 },
      { op: 'scaleAmount', abilityId: S.chainLightning, effectType: 'damage', mult: 0.8 },
      { op: 'modifyArea', abilityId: S.tempest, maxTargetsDelta: 1 },
    ],
  }),
  node({
    id: 'stormcaller.t1.walking_bolt',
    name: 'Static Stride',
    description: 'Lightning Bolt and Tempest can be cast while moving.',
    tier: 1,
    col: 1,
    cost: 3,
    effect: [
      { op: 'patchAbility', abilityId: S.lightningBolt, patch: { canCastWhileMoving: true } },
      { op: 'patchAbility', abilityId: S.tempest, patch: { canCastWhileMoving: true } },
    ],
  }),
  node({
    id: 'stormcaller.t1.lava_flow',
    name: 'Lava Flow',
    description: 'Lava Surge stacks up to 2 times, and each stack also grants a Lava Burst charge. Spend both instants or bank one.',
    tier: 1,
    col: 2,
    cost: 2,
    effect: [{ op: 'patchAura', auraId: AURA.lavaSurge, patch: { stacking: 'stack', maxStacks: 2 } }],
  }),
  node({
    id: 'stormcaller.t1.thundering_shear',
    name: 'Thundering Shear',
    description: 'Wind Shear recharges in 10 s and generates 10 Maelstrom and 1 Static Charge.',
    tier: 1,
    col: 3,
    cost: 1,
    effect: [
      { op: 'modifyCooldown', abilityId: S.windShear, durationMs: 10000 },
      { op: 'modifyResource', abilityId: S.windShear, field: 'gain', resource: 'maelstrom', amount: 10 },
      { op: 'addEffects', abilityId: S.windShear, effects: [staticCharge()] },
    ],
  }),
  node({
    id: 'stormcaller.t1.spreading_flame',
    name: 'Spreading Flame',
    description: 'Flame Shock also spreads to up to 2 other enemies within 10 yd of the target.',
    tier: 1,
    col: 4,
    cost: 2,
    effect: [{ op: 'addEffects', abilityId: S.flameShock, effects: [{ type: 'applyAura', to: 'area', area: nearTarget(10, 2), aura: FLAME_SHOCK }] }],
  }),
  node({
    id: 'stormcaller.t1.aftershock',
    name: 'Aftershock',
    description: 'Earth Shock and Earthquake have a 15% chance to refund 25 Maelstrom. The chance rises by 10% after each miss.',
    tier: 1,
    col: 5,
    cost: 2,
    effect: [
      procPassive('talent.stormcaller.aftershock', 'Aftershock', {
        on: 'cast',
        abilityIds: [S.earthShock, S.earthquake],
        chance: 0.15,
        badLuck: { step: 0.1 },
        effects: [resource('maelstrom', 25)],
      }),
    ],
  }),
  node({
    id: 'stormcaller.t1.spirit_walk',
    name: 'Spirit Walk',
    description: 'Astral Shift also increases your movement speed by 40%.',
    tier: 1,
    col: 6,
    cost: 1,
    effect: [
      {
        op: 'patchAura',
        auraId: AURA.astralShift,
        patch: {
          mods: [
            { stat: 'damageTakenPct', value: -0.4 },
            { stat: 'moveSpeedPct', value: 0.4 },
          ],
        },
      },
    ],
  }),

  // ---------------------------------------------------------------- tier 2
  node({
    id: 'stormcaller.t2.surge_of_power',
    name: 'Surge of Power',
    description: 'Earth Shock makes your next Lightning Bolt within 10 s instant.',
    tier: 2,
    col: 0,
    cost: 2,
    effect: [{ op: 'addEffects', abilityId: S.earthShock, effects: [selfApply(SURGE_OF_POWER)] }],
  }),
  node({
    id: 'stormcaller.t2.static_discharge',
    name: 'Static Discharge',
    description: 'Lightning Bolt and Tempest critical strikes generate 3 extra Maelstrom and 1 extra Static Charge.',
    tier: 2,
    col: 1,
    cost: 1,
    effect: [
      procPassive('talent.stormcaller.static_discharge', 'Static Discharge', {
        on: 'critDealt',
        abilityIds: [S.lightningBolt, S.tempest],
        // Once per cast: Tempest's cleave crits land in the same tick.
        icdMs: 500,
        effects: [resource('maelstrom', 3), staticCharge()],
      }),
    ],
  }),
  node({
    id: 'stormcaller.t2.capacitor',
    name: 'Capacitor',
    description: 'Wind Shear also interrupts up to 2 other casting enemies within 8 yd of the target.',
    tier: 2,
    col: 2,
    cost: 3,
    prereqs: ['stormcaller.t1.thundering_shear'],
    effect: [{ op: 'addEffects', abilityId: S.windShear, effects: [{ type: 'interrupt', to: 'area', area: nearTarget(8, 2), lockoutMs: 3000 }] }],
  }),
  node({
    id: 'stormcaller.t2.volcanic_stride',
    name: 'Volcanic Stride',
    description: 'Lava Burst can be cast while moving.',
    tier: 2,
    col: 3,
    cost: 2,
    effect: [{ op: 'patchAbility', abilityId: S.lavaBurst, patch: { canCastWhileMoving: true } }],
  }),
  node({
    id: 'stormcaller.t2.earthshaker',
    name: 'Earthshaker',
    description: 'Earthquake stuns enemies in the area for 1 s when it lands.',
    tier: 2,
    col: 4,
    cost: 2,
    effect: [
      {
        op: 'addEffects',
        abilityId: S.earthquake,
        effects: [
          {
            type: 'applyAura',
            to: 'area',
            area: { anchor: 'point', shape: { type: 'circle', radius: 8 }, affects: 'enemies', maxTargets: 8 },
            aura: stun('talent.stormcaller.earthshaker', 'Earthshaker', 1000),
          },
        ],
      },
    ],
  }),
  node({
    id: 'stormcaller.t2.ancestral_guidance',
    name: 'Ancestral Guidance',
    description: 'Astral Shift also heals you for 15% of your max HP.',
    tier: 2,
    col: 5,
    cost: 1,
    effect: [{ op: 'addEffects', abilityId: S.astralShift, effects: [{ type: 'heal', to: 'self', amount: { pctSourceMaxHp: 0.15 } }] }],
  }),

  // ---------------------------------------------------------------- tier 3
  node({
    id: 'stormcaller.t3.echo_of_elements',
    name: 'Echo of the Elements',
    description: 'Lava Burst has 3 charges, and a Lava Surge that procs while one is already active refunds 10 Maelstrom instead of being wasted.',
    tier: 3,
    col: 0,
    cost: 3,
    effect: [
      { op: 'modifyCooldown', abilityId: S.lavaBurst, charges: 3 },
      { op: 'patchAura', auraId: PASSIVE.stormLavaSurge, patch: { procs: [lavaSurgeRollWithEcho] } },
      { op: 'addEffects', abilityId: S.arcTorrent, list: 'channelEndEffects', position: 'start', effects: [echoRefund] },
    ],
  }),
  node({
    id: 'stormcaller.t3.thundercrash',
    name: 'Thundercrash',
    description: 'After a full Arc Torrent channel, Arc Torrent becomes Thundercrash for 8 s: instant, 6.0× nature damage to the target and 3.0× to nearby enemies, 20 Maelstrom.',
    tier: 3,
    col: 1,
    cost: 3,
    effect: [
      { op: 'addAbility', ability: THUNDERCRASH },
      { op: 'addEffects', abilityId: S.arcTorrent, list: 'channelEndEffects', effects: [selfApply(THUNDERCRASH_READY)] },
    ],
  }),
  node({
    id: 'stormcaller.t3.storm_unleashed',
    name: 'Storm Unleashed',
    description: 'Capstone. Earthquake costs 45 Maelstrom, covers a 25% larger area and counts as 2 spenders toward Tempest.',
    tier: 3,
    col: 2,
    cost: 3,
    choiceGroup: STORMCALLER_CAPSTONE,
    effect: [
      { op: 'modifyResource', abilityId: S.earthquake, field: 'cost', resource: 'maelstrom', amount: 45 },
      { op: 'modifyArea', abilityId: S.earthquake, radiusMult: 1.25 },
      // One extra Gathering Storm stack before the quake's own step (never past the cap).
      {
        op: 'addEffects',
        abilityId: S.earthquake,
        position: 'start',
        effects: [
          {
            type: 'conditional',
            cond: { type: 'not', cond: { type: 'selfHasAura', auraId: AURA.gatheringStorm, minStacks: 2 } },
            then: [selfApply(auraRef(AURA.gatheringStorm))],
          },
        ],
      },
    ],
  }),
  node({
    id: 'stormcaller.t3.master_of_flame',
    name: 'Master of Flame',
    description: 'Capstone. Lava Burst recharges 2 s faster and its critical strikes generate 8 Maelstrom. Master of the Elements also empowers Lightning Bolt.',
    tier: 3,
    col: 3,
    cost: 3,
    choiceGroup: STORMCALLER_CAPSTONE,
    effect: [
      { op: 'modifyCooldown', abilityId: S.lavaBurst, deltaMs: -2000 },
      procPassive('talent.stormcaller.master_of_flame', 'Master of Flame', { on: 'critDealt', abilityIds: [S.lavaBurst], effects: [resource('maelstrom', 8)] }),
      {
        op: 'patchAura',
        auraId: AURA.masterOfElements,
        patch: {
          mods: [{ stat: 'damageDonePct', value: 0.2, abilityIds: MOTE_WITH_BOLT }],
          procs: [{ on: 'castResolved', abilityIds: MOTE_WITH_BOLT, consumeStack: true, effects: [] }],
        },
      },
    ],
  }),
  node({
    id: 'stormcaller.t3.ascendance',
    name: 'Ascendance',
    description: 'Capstone. Stormkeeper also grants Ascendance for 12 s: Lava Burst becomes Lava Beam, an instant with no cooldown that hits the target and 4 nearby enemies.',
    tier: 3,
    col: 4,
    cost: 3,
    choiceGroup: STORMCALLER_CAPSTONE,
    effect: [
      { op: 'addAbility', ability: LAVA_BEAM },
      { op: 'addEffects', abilityId: S.stormkeeper, effects: [selfApply(ASCENDANCE)] },
    ],
  }),
];
